import { useState, useCallback, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AppLayout } from '@/components/layout/app-layout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { FadeIn } from '@/components/motion';
import { motion, AnimatePresence } from 'motion/react';
import { cn } from '@/lib/utils/cn';
import {
  BookOpen,
  ChevronLeft,
  ChevronRight,
  Settings,
  ArrowRight,
  RefreshCw,
  Sparkles,
  Database,
  LayoutTemplate,
  FileText,
  X,
} from 'lucide-react';

import { useOnboardingStore } from '@/features/onboarding/store/onboarding-store';
import { SEGMENT_MAP } from '@/features/onboarding/data/segments';

import { CHAPTERS } from '../data/chapters';
import {
  PERSONAS,
  MODELO_SOFTWARE,
  MODELO_SOPORTE,
  MATERIALS,
  DEMO_SCENARIO,
  LOB_HIERARCHY,
} from '../data/tutorial-data';
import {
  QUIZ_CALCULANDO,
  QUIZ_APROBACION,
  QUIZ_FINAL,
  CHALLENGE_CONFIGURACION,
  CHALLENGE_PREPARACION,
  CHALLENGE_APROBACION,
} from '../data/quiz-data';

import { ChapterNav } from '../components/chapter-nav';
import { StoryHeader } from '../components/story-header';
import { TryItCta } from '../components/try-it-cta';
import { FormulaPlayground } from '../components/formula-playground';
import { WaterfallDemo } from '../components/waterfall-demo';
import { MarginDemo } from '../components/margin-demo';
import { PersonaSwitcher } from '../components/persona-switcher';
import { WorkflowDiagram } from '../components/workflow-diagram';
import { ChallengeCard } from '../components/challenge-card';
import { QuickQuiz } from '../components/quick-quiz';

// ── Helper components ─────────────────────────────────────────────

function NumberedStep({ number, children }: { number: number; children: React.ReactNode }) {
  return (
    <div className="flex gap-3 items-start">
      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">
        {number}
      </div>
      <div className="pt-0.5 text-sm text-muted-foreground">{children}</div>
    </div>
  );
}

// ── Welcome Banner (first-time users) ────────────────────────────

const SETUP_STEPS = [
  {
    icon: Database,
    label: 'Datos maestros',
    description: 'Monedas, países y líneas de negocio',
    href: '/master-data',
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
  },
  {
    icon: LayoutTemplate,
    label: 'Modelo de precios',
    description: 'Variables y fórmulas para calcular',
    href: '/pricing-models',
    color: 'text-violet-600',
    bg: 'bg-violet-50',
    border: 'border-violet-200',
  },
  {
    icon: FileText,
    label: 'Primera cotización',
    description: 'Crea tu primera propuesta comercial',
    href: '/quotes/new',
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
  },
];

function WelcomeBanner({ onDismiss }: { onDismiss: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.35, ease: 'easeOut' }}
      className="relative rounded-xl border border-primary/20 bg-gradient-to-br from-primary/5 via-blue-50/60 to-violet-50/40 p-5 mb-4 overflow-hidden"
    >
      {/* Decorative orb */}
      <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-primary/10 blur-2xl pointer-events-none" />

      <button
        onClick={onDismiss}
        className="absolute top-3 right-3 p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-black/5 transition-colors"
        aria-label="Cerrar"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-5 h-5 text-primary" />
        </div>
        <div>
          <p className="font-semibold text-sm">¡Bienvenido a Prixia!</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Antes de crear cotizaciones, configura estos 3 elementos en orden:
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {SETUP_STEPS.map((step, i) => {
          const Icon = step.icon;
          return (
            <Link
              key={step.href}
              to={step.href}
              className={cn(
                'flex items-start gap-3 rounded-lg border p-3 transition-all hover:shadow-sm hover:scale-[1.02]',
                step.bg,
                step.border
              )}
            >
              <div
                className={cn(
                  'w-7 h-7 rounded-md bg-white flex items-center justify-center flex-shrink-0 shadow-sm'
                )}
              >
                <Icon className={cn('w-4 h-4', step.color)} />
              </div>
              <div>
                <p className="text-xs font-semibold flex items-center gap-1">
                  <span
                    className={cn(
                      'text-[10px] font-bold w-4 h-4 rounded-full bg-white shadow-sm flex items-center justify-center flex-shrink-0',
                      step.color
                    )}
                  >
                    {i + 1}
                  </span>
                  {step.label}
                </p>
                <p className="text-[11px] text-muted-foreground mt-0.5">{step.description}</p>
              </div>
            </Link>
          );
        })}
      </div>

      <p className="text-[11px] text-muted-foreground mt-3">
        💡 Puedes volver al tutorial cuando quieras desde el menú lateral.
      </p>
    </motion.div>
  );
}

// ── Segment Banner ────────────────────────────────────────────────

function SegmentBanner({ onChangeSegment }: { onChangeSegment: () => void }) {
  const { segment } = useOnboardingStore();
  if (!segment) return null;
  const segDef = SEGMENT_MAP[segment];
  return (
    <div
      className={cn(
        'flex items-center justify-between gap-3 rounded-xl px-4 py-3 border mb-2',
        segDef.bgClass,
        segDef.borderClass
      )}
    >
      <div className="flex items-center gap-2.5">
        <span className="text-xl">{segDef.emoji}</span>
        <div>
          <p className={cn('text-sm font-semibold', segDef.textClass)}>
            Tutorial para {segDef.title}
          </p>
          <p className="text-xs text-muted-foreground">
            {segDef.tutorialCompany} — {segDef.subtitle}
          </p>
        </div>
      </div>
      <button
        onClick={onChangeSegment}
        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
        title="Cambiar segmento"
      >
        <RefreshCw className="w-3 h-3" />
        Cambiar
      </button>
    </div>
  );
}

// ── Chapter 0: Bienvenida ─────────────────────────────────────────

function ChapterIntro() {
  const { segment } = useOnboardingStore();
  const segDef = segment ? SEGMENT_MAP[segment] : null;

  // Determine personas to show
  const personas = segDef
    ? [segDef.personas.admin, segDef.personas.sales, segDef.personas.approver]
    : [
        {
          name: 'Carlos',
          role: 'Administrador',
          description:
            'Configura modelos de pricing, datos maestros y gestiona el equipo de ACME Corporation.',
          initials: 'CA',
          color: 'bg-orange-500',
        },
        {
          name: 'María',
          role: 'Vendedora',
          description: 'Crea cotizaciones para clientes usando los modelos que Carlos configura.',
          initials: 'MV',
          color: 'bg-blue-500',
        },
        {
          name: 'Juan',
          role: 'Aprobador',
          description: 'Revisa y aprueba (o rechaza) las cotizaciones que envían los vendedores.',
          initials: 'JA',
          color: 'bg-green-500',
        },
      ];

  const companyName = segDef?.tutorialCompany ?? 'ACME Corporation';
  const industryDesc = segDef
    ? `${segDef.subtitle}. El equipo usa Prixia para ${segDef.tutorialScenario.toLowerCase()}`
    : 'tres personas y una asistente IA trabajan juntas para cotizar proyectos de tecnología';

  const flowColors = ['border-orange-500', 'border-blue-500', 'border-green-500'];
  const flowLabels = ['Configurar', 'Cotizar', 'Aprobar'];

  return (
    <div className="space-y-6">
      <p className="text-muted-foreground">
        En <strong className="text-foreground">{companyName}</strong>, {industryDesc}. Cada persona
        tiene un rol distinto en el flujo. Acompañarlos en esta historia te ayudará a entender todo
        el sistema.
      </p>

      {/* Persona cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {personas.map((p) => (
          <Card key={p.initials} className="overflow-hidden">
            <div className={cn('h-2', p.color)} />
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-3">
                <div
                  className={cn(
                    'w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg',
                    p.color
                  )}
                >
                  {p.initials}
                </div>
                <div>
                  <p className="font-semibold">{p.name}</p>
                  <Badge variant="outline" className="text-xs">
                    {p.role}
                  </Badge>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">{p.description}</p>
              {/* Show credentials only for default tecnologia segment */}
              {(!segment || segment === 'tecnologia') && (
                <div className="bg-muted rounded px-3 py-1.5">
                  <code className="text-xs">
                    {p.initials === 'CA'
                      ? 'admin@acme.com'
                      : p.initials === 'MV'
                        ? 'maria.vendedor@acme.com'
                        : 'juan.aprobador@acme.com'}
                    {' / password123'}
                  </code>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Flow diagram */}
      <div className="flex items-center justify-center gap-3 py-4 flex-wrap">
        {personas.map((p, i) => (
          <div key={p.initials} className="flex items-center gap-3">
            <div
              className={cn(
                'flex items-center gap-2 px-4 py-2 rounded-lg border-2 bg-background',
                flowColors[i]
              )}
            >
              <div
                className={cn(
                  'w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold',
                  p.color
                )}
              >
                {p.initials}
              </div>
              <span className="text-sm font-medium">{flowLabels[i]}</span>
            </div>
            {i < 2 && <ArrowRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Chapter 1: Configuracion ──────────────────────────────────────

function ChapterConfiguracion() {
  return (
    <div className="space-y-6">
      <StoryHeader
        persona={PERSONAS.carlos}
        quote="Antes de que el equipo pueda cotizar, necesito configurar los modelos de pricing y los datos maestros."
      />

      {/* Models showcase */}
      <div>
        <h3 className="text-sm font-semibold mb-3">Modelos de Pricing Disponibles</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[MODELO_SOFTWARE, MODELO_SOPORTE].map((m) => (
            <Card key={m.name}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="font-semibold">{m.name}</p>
                  <Badge variant="secondary" className="text-xs">
                    {m.variables.length} variables
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">{m.description}</p>
                <div className="flex gap-3 text-xs text-muted-foreground">
                  <span>
                    Margen objetivo: <strong className="text-green-600">{m.targetMargin}%</strong>
                  </span>
                  <span>
                    Piso: <strong className="text-red-600">{m.floorMargin}%</strong>
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Variables table */}
      <div>
        <h3 className="text-sm font-semibold mb-3">Variables del Modelo Software</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 pr-4 font-medium">#</th>
                <th className="text-left py-2 pr-4 font-medium">Codigo</th>
                <th className="text-left py-2 pr-4 font-medium">Nombre</th>
                <th className="text-left py-2 pr-4 font-medium">Tipo</th>
                <th className="text-left py-2 font-medium">Formula / Default</th>
              </tr>
            </thead>
            <tbody>
              {MODELO_SOFTWARE.variables.map((v) => (
                <tr key={v.code} className="border-b border-border/50">
                  <td className="py-1.5 pr-4 text-muted-foreground">{v.order}</td>
                  <td className="py-1.5 pr-4">
                    <code className="text-xs font-mono text-primary">{v.code}</code>
                  </td>
                  <td className="py-1.5 pr-4 text-muted-foreground">{v.name}</td>
                  <td className="py-1.5 pr-4">
                    <Badge
                      variant={v.variableType === 'CALCULATED' ? 'default' : 'outline'}
                      className="font-mono text-xs"
                    >
                      {v.variableType}
                    </Badge>
                  </td>
                  <td className="py-1.5">
                    {v.formula ? (
                      <code className="text-xs text-muted-foreground font-mono bg-muted px-1.5 py-0.5 rounded">
                        {v.formula}
                      </code>
                    ) : (
                      <span className="text-xs text-muted-foreground">{v.defaultValue ?? '—'}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* LOB hierarchy */}
      <div>
        <h3 className="text-sm font-semibold mb-3">Lineas de Negocio (LOB)</h3>
        <div className="bg-muted/50 rounded-lg p-4">
          {LOB_HIERARCHY.map((sector) => (
            <div key={sector.name}>
              <p className="font-medium text-sm">
                {sector.name}{' '}
                <Badge variant="outline" className="text-[10px]">
                  SECTOR
                </Badge>
              </p>
              <div className="ml-4 mt-1 space-y-1">
                {sector.children.map((vertical) => (
                  <div key={vertical.name}>
                    <p className="text-sm text-muted-foreground">
                      └ {vertical.name}{' '}
                      <Badge variant="outline" className="text-[10px]">
                        VERTICAL
                      </Badge>
                    </p>
                    <div className="ml-6 space-y-0.5">
                      {vertical.children.map((product) => (
                        <p key={product.name} className="text-xs text-muted-foreground">
                          └ {product.name}{' '}
                          <Badge variant="outline" className="text-[10px]">
                            PRODUCT
                          </Badge>
                        </p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <TryItCta to="/pricing-models" label="Ir a Modelos de Pricing" />

      <ChallengeCard challenge={CHALLENGE_CONFIGURACION} />
    </div>
  );
}

// ── Chapter 2: Preparando la Cotizacion ───────────────────────────

function ChapterPreparacion() {
  return (
    <div className="space-y-6">
      <StoryHeader
        persona={PERSONAS.maria}
        quote="Recibi un email de Banco Nacional. Necesitan 50 licencias de Microsoft 365. Voy a crear una cotizacion."
      />

      <Tabs defaultValue="formulario">
        <TabsList>
          <TabsTrigger value="formulario">Via Formulario</TabsTrigger>
        </TabsList>

        <TabsContent value="formulario" className="space-y-6">
          {/* Mockup form */}
          <Card>
            <CardContent className="p-4 space-y-4">
              <h3 className="text-sm font-semibold">Formulario de Nueva Cotizacion</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { label: 'Cliente', value: DEMO_SCENARIO.client },
                  { label: 'Email', value: DEMO_SCENARIO.clientEmail },
                  { label: 'Divisa', value: DEMO_SCENARIO.currency },
                  { label: 'Modelo', value: DEMO_SCENARIO.model },
                ].map((field) => (
                  <div key={field.label} className="space-y-1">
                    <label className="text-xs font-medium text-muted-foreground">
                      {field.label}
                    </label>
                    <div className="px-3 py-2 rounded-md border bg-muted/50 text-sm">
                      {field.value}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Steps */}
          <div>
            <h3 className="text-sm font-semibold mb-3">Pasos para crear la cotizacion</h3>
            <div className="space-y-3">
              <NumberedStep number={1}>
                Ir a <strong className="text-foreground">Cotizaciones</strong> y clic en "Nueva
                Cotizacion"
              </NumberedStep>
              <NumberedStep number={2}>
                Llenar los datos del cliente:{' '}
                <strong className="text-foreground">{DEMO_SCENARIO.client}</strong>, email y divisa
              </NumberedStep>
              <NumberedStep number={3}>
                Agregar una seccion y seleccionar el modelo{' '}
                <strong className="text-foreground">{DEMO_SCENARIO.model}</strong>
              </NumberedStep>
              <NumberedStep number={4}>
                Agregar un line item con el material{' '}
                <strong className="text-foreground">{DEMO_SCENARIO.material}</strong>
              </NumberedStep>
              <NumberedStep number={5}>
                Completar las variables: cantidad={DEMO_SCENARIO.inputs.CANTIDAD}, costo=$
                {DEMO_SCENARIO.inputs.COSTO_LICENCIA}, descuento=
                {DEMO_SCENARIO.inputs.DESCUENTO_VOLUMEN}%
              </NumberedStep>
              <NumberedStep number={6}>Revisar el calculo y enviar a aprobacion</NumberedStep>
            </div>
          </div>

          {/* Line items preview */}
          <Card>
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold mb-3">Seccion 1: {DEMO_SCENARIO.model}</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 pr-4 font-medium">Material</th>
                      <th className="text-right py-2 pr-4 font-medium">Cantidad</th>
                      <th className="text-right py-2 pr-4 font-medium">Costo/u</th>
                      <th className="text-right py-2 pr-4 font-medium">Descuento</th>
                      <th className="text-right py-2 font-medium">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-border/50">
                      <td className="py-2 pr-4">
                        <div>
                          <p className="font-medium">{DEMO_SCENARIO.material}</p>
                          <p className="text-xs text-muted-foreground">
                            {MATERIALS[0].externalCode}
                          </p>
                        </div>
                      </td>
                      <td className="py-2 pr-4 text-right font-mono">
                        {DEMO_SCENARIO.inputs.CANTIDAD}
                      </td>
                      <td className="py-2 pr-4 text-right font-mono">
                        ${DEMO_SCENARIO.inputs.COSTO_LICENCIA}
                      </td>
                      <td className="py-2 pr-4 text-right font-mono">
                        {DEMO_SCENARIO.inputs.DESCUENTO_VOLUMEN}%
                      </td>
                      <td className="py-2 text-right font-mono font-semibold">
                        ${DEMO_SCENARIO.calculated.TOTAL_LICENCIAS.toLocaleString()}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="mt-3 pt-3 border-t flex justify-between text-sm">
                <span className="text-muted-foreground">
                  + Implementacion: ${DEMO_SCENARIO.inputs.COSTO_IMPLEMENTACION.toLocaleString()}
                </span>
                <span className="text-muted-foreground">
                  + Soporte: ${DEMO_SCENARIO.inputs.COSTO_SOPORTE_ANUAL.toLocaleString()}
                </span>
                <span className="font-semibold">
                  Subtotal: ${DEMO_SCENARIO.calculated.SUBTOTAL_SOFTWARE.toLocaleString()}
                </span>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <TryItCta to="/quotes/new" label="Crear una Cotizacion" />
      <ChallengeCard challenge={CHALLENGE_PREPARACION} />
    </div>
  );
}

// ── Chapter 3: Calculando Precios ─────────────────────────────────

function ChapterCalculando() {
  return (
    <div className="space-y-8">
      <StoryHeader
        persona={PERSONAS.maria}
        quote="Mira como cambian los numeros cuando ajusto las variables. El sistema calcula todo en cascada automaticamente."
      />

      {/* Formula Playground */}
      <div>
        <h3 className="text-base font-semibold mb-4">Calculadora Interactiva</h3>
        <FormulaPlayground />
      </div>

      {/* Waterfall */}
      <div>
        <h3 className="text-base font-semibold mb-4">Cascada de Costos</h3>
        <WaterfallDemo />
      </div>

      {/* Margin demo */}
      <div>
        <h3 className="text-base font-semibold mb-4">Indicador de Margen</h3>
        <MarginDemo />
      </div>

      <TryItCta to="/quotes/new" label="Crear tu propia cotizacion" />

      <QuickQuiz quiz={QUIZ_CALCULANDO} />
    </div>
  );
}

// ── Chapter 4: Aprobacion ─────────────────────────────────────────

function ChapterAprobacion() {
  return (
    <div className="space-y-6">
      <StoryHeader
        persona={PERSONAS.juan}
        quote="Maria me envio una cotizacion para Banco Nacional. Voy a revisar los numeros y decidir."
      />

      {/* Persona switcher */}
      <PersonaSwitcher personas={['maria', 'juan']}>
        {(active) =>
          active === 'maria' ? (
            <Card>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Badge variant="warning" className="text-xs">
                    PENDIENTE APROBACION
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  Maria ve que su cotizacion{' '}
                  <strong className="text-foreground">QT-2026-001</strong> para{' '}
                  <strong className="text-foreground">{DEMO_SCENARIO.client}</strong> esta pendiente
                  de aprobacion. No puede modificarla mientras esta en revision.
                </p>
                <div className="bg-muted/50 rounded-lg p-3 text-sm">
                  <p>
                    Total:{' '}
                    <strong>${DEMO_SCENARIO.calculated.PRECIO_FINAL_TOTAL.toLocaleString()}</strong>
                  </p>
                  <p className="text-muted-foreground">
                    Margen: {DEMO_SCENARIO.inputs.MARGEN_PORCENTAJE}%
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Badge className="text-xs bg-yellow-500">1 PENDIENTE</Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  Juan ve la cotizacion en su cola de aprobaciones. Puede revisar todos los
                  detalles: secciones, items, cascada de costos y margen antes de decidir.
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { label: 'Aprobar', color: 'bg-green-500', desc: 'Todo en orden' },
                    { label: 'Rechazar', color: 'bg-red-500', desc: 'No viable' },
                    { label: 'Ajuste', color: 'bg-orange-500', desc: 'Modificar margen' },
                  ].map((action) => (
                    <div
                      key={action.label}
                      className={cn('rounded-lg p-3 text-white text-center', action.color)}
                    >
                      <p className="font-semibold text-sm">{action.label}</p>
                      <p className="text-xs opacity-80">{action.desc}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )
        }
      </PersonaSwitcher>

      {/* Workflow */}
      <div>
        <h3 className="text-sm font-semibold mb-3">Diagrama de Flujo</h3>
        <WorkflowDiagram />
      </div>

      <TryItCta to="/approvals" label="Ir a Aprobaciones" />

      <ChallengeCard challenge={CHALLENGE_APROBACION} />
      <QuickQuiz quiz={QUIZ_APROBACION} />
    </div>
  );
}

// ── Chapter 7: Tips Avanzados ─────────────────────────────────────

function ChapterAvanzado() {
  return (
    <div className="space-y-6">
      <p className="text-muted-foreground">
        Funcionalidades avanzadas para sacar el maximo provecho de la plataforma.
      </p>

      {/* Multi-section */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <h3 className="text-sm font-semibold">Cotizaciones Multi-Seccion</h3>
          <p className="text-sm text-muted-foreground">
            Una cotizacion puede tener{' '}
            <strong className="text-foreground">multiples secciones</strong>, cada una con un modelo
            de pricing diferente. Por ejemplo:
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 bg-blue-50 dark:bg-blue-950/30 rounded-lg p-3 border border-blue-200 dark:border-blue-800">
              <p className="text-sm font-medium text-blue-700 dark:text-blue-400">Seccion 1</p>
              <p className="text-xs text-muted-foreground">Modelo Software — licencias</p>
            </div>
            <div className="flex items-center justify-center">
              <span className="text-xl text-muted-foreground">+</span>
            </div>
            <div className="flex-1 bg-violet-50 dark:bg-violet-950/30 rounded-lg p-3 border border-violet-200 dark:border-violet-800">
              <p className="text-sm font-medium text-violet-700 dark:text-violet-400">Seccion 2</p>
              <p className="text-xs text-muted-foreground">Modelo Soporte — horas tecnicas</p>
            </div>
            <div className="flex items-center justify-center">
              <span className="text-xl text-muted-foreground">=</span>
            </div>
            <div className="flex-1 bg-green-50 dark:bg-green-950/30 rounded-lg p-3 border border-green-200 dark:border-green-800">
              <p className="text-sm font-medium text-green-700 dark:text-green-400">Total</p>
              <p className="text-xs text-muted-foreground">Suma de ambas secciones</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lookup tables */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <h3 className="text-sm font-semibold">Tablas de Busqueda (Lookup)</h3>
          <p className="text-sm text-muted-foreground">
            Las variables tipo{' '}
            <Badge variant="outline" className="text-xs font-mono">
              LOOKUP
            </Badge>{' '}
            cruzan dos dimensiones para obtener un valor. Ejemplo: descuento segun region y volumen.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border">
              <thead>
                <tr className="bg-muted/50">
                  <th className="border p-2 text-left">Region / Volumen</th>
                  <th className="border p-2 text-center">1-10</th>
                  <th className="border p-2 text-center">11-50</th>
                  <th className="border p-2 text-center">51+</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { region: 'Norteamerica', values: ['5%', '10%', '15%'] },
                  { region: 'Latinoamerica', values: ['3%', '7%', '12%'] },
                  { region: 'Europa', values: ['4%', '8%', '13%'] },
                ].map((row) => (
                  <tr key={row.region}>
                    <td className="border p-2 font-medium">{row.region}</td>
                    {row.values.map((v, i) => (
                      <td key={i} className="border p-2 text-center text-muted-foreground">
                        {v}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-wrap gap-3 mt-6 pt-4 border-t">
        <TryItCta to="/quotes/new" label="Crear Cotizacion" />
        <TryItCta to="/pricing-models" label="Ver Modelos" />
        <TryItCta to="/master-data" label="Datos Maestros" />
      </div>

      <QuickQuiz quiz={QUIZ_FINAL} />
    </div>
  );
}

// ── Chapter content map ───────────────────────────────────────────

const CHAPTER_CONTENT: Record<string, () => JSX.Element> = {
  intro: ChapterIntro,
  configuracion: ChapterConfiguracion,
  preparacion: ChapterPreparacion,
  calculando: ChapterCalculando,
  aprobacion: ChapterAprobacion,
  avanzado: ChapterAvanzado,
};

// ── Main Page ─────────────────────────────────────────────────────

export function TutorialPage() {
  const navigate = useNavigate();
  const [currentChapter, setCurrentChapter] = useState(0);
  const [completedChapters, setCompletedChapters] = useState<Set<string>>(new Set());
  const { segment, reset: resetOnboarding, isFirstTime, clearFirstTime } = useOnboardingStore();
  const segDef = segment ? SEGMENT_MAP[segment] : null;
  const [showWelcome, setShowWelcome] = useState(isFirstTime);

  // Clear the first-time flag after the user sees the banner
  useEffect(() => {
    if (isFirstTime) clearFirstTime();
  }, []);

  const chapter = CHAPTERS[currentChapter];
  const Content = CHAPTER_CONTENT[chapter.id];

  // Resolve persona for chapter badge (segment-aware)
  const persona = (() => {
    if (!chapter.persona) return null;
    if (chapter.persona === 'luna') return { color: 'bg-violet-500', initials: 'IA' };
    if (segDef) {
      if (chapter.persona === 'carlos')
        return { color: segDef.personas.admin.color, initials: segDef.personas.admin.initials };
      if (chapter.persona === 'maria')
        return { color: segDef.personas.sales.color, initials: segDef.personas.sales.initials };
      if (chapter.persona === 'juan')
        return {
          color: segDef.personas.approver.color,
          initials: segDef.personas.approver.initials,
        };
    }
    const p = PERSONAS[chapter.persona];
    return p ? { color: p.color, initials: p.initials } : null;
  })();

  const navigateTo = useCallback(
    (index: number) => {
      // Mark current as completed when moving forward
      if (index > currentChapter) {
        setCompletedChapters((prev) => {
          const next = new Set(prev);
          next.add(CHAPTERS[currentChapter].id);
          return next;
        });
      }
      setCurrentChapter(index);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [currentChapter]
  );

  const goNext = () => {
    if (currentChapter < CHAPTERS.length - 1) navigateTo(currentChapter + 1);
  };

  const goPrev = () => {
    if (currentChapter > 0) navigateTo(currentChapter - 1);
  };

  const handleChangeSegment = () => {
    resetOnboarding();
    navigate('/dashboard');
  };

  const tutorialTitle = segDef
    ? `Historia: ${segDef.tutorialCompany}`
    : 'Historia de una Cotizacion';
  const tutorialSubtitle = segDef
    ? `Tutorial para ${segDef.title} — ${segDef.personas.admin.name}, ${segDef.personas.sales.name}, ${segDef.personas.approver.name} y Luna`
    : 'Tutorial interactivo — aprende siguiendo a Carlos, Maria, Juan y Luna';

  return (
    <AppLayout>
      <div className="container mx-auto p-4 sm:p-8">
        {/* Header */}
        <FadeIn direction="down">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{tutorialTitle}</h1>
              <p className="text-muted-foreground text-sm">{tutorialSubtitle}</p>
            </div>
          </div>
          {/* Welcome banner (first-time users) */}
          <AnimatePresence>
            {showWelcome && <WelcomeBanner onDismiss={() => setShowWelcome(false)} />}
          </AnimatePresence>

          {/* Segment banner */}
          <SegmentBanner onChangeSegment={handleChangeSegment} />
        </FadeIn>

        {/* Chapter stepper */}
        <div className="mb-8">
          <ChapterNav
            currentChapter={currentChapter}
            completedChapters={completedChapters}
            onNavigate={navigateTo}
          />
        </div>

        <div className="flex gap-8">
          {/* Sidebar (desktop) */}
          <div className="hidden lg:block w-56 flex-shrink-0">
            <div className="sticky top-24 space-y-1">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                Capitulos
              </p>
              {CHAPTERS.map((ch, i) => {
                // Resolve persona: use segment-specific if available
                const resolvePersona = (id: string | null) => {
                  if (!id) return null;
                  if (id === 'luna') return { color: 'bg-violet-500', initials: 'IA' };
                  if (segDef) {
                    if (id === 'carlos')
                      return {
                        color: segDef.personas.admin.color,
                        initials: segDef.personas.admin.initials,
                      };
                    if (id === 'maria')
                      return {
                        color: segDef.personas.sales.color,
                        initials: segDef.personas.sales.initials,
                      };
                    if (id === 'juan')
                      return {
                        color: segDef.personas.approver.color,
                        initials: segDef.personas.approver.initials,
                      };
                  }
                  const p = PERSONAS[id];
                  return p ? { color: p.color, initials: p.initials } : null;
                };
                const chPersona = resolvePersona(ch.persona);
                const isActive = i === currentChapter;
                const isCompleted = completedChapters.has(ch.id);
                return (
                  <button
                    key={ch.id}
                    onClick={() => navigateTo(i)}
                    className={cn(
                      'flex items-center gap-2 w-full text-left px-3 py-2 rounded-md text-sm transition-colors',
                      isActive
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                    )}
                  >
                    {chPersona ? (
                      <div
                        className={cn(
                          'w-5 h-5 rounded-full flex items-center justify-center text-white text-[9px] font-bold flex-shrink-0',
                          chPersona.color
                        )}
                      >
                        {chPersona.initials}
                      </div>
                    ) : (
                      <Settings className="w-4 h-4 flex-shrink-0" />
                    )}
                    <span className="truncate">{ch.title}</span>
                    {isCompleted && <span className="ml-auto text-green-500 text-xs">✓</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Main content */}
          <div className="flex-1 min-w-0">
            <AnimatePresence mode="wait">
              <motion.div
                key={chapter.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.3 }}
              >
                {/* Chapter title */}
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="outline" className="text-xs">
                      Capitulo {chapter.index}
                    </Badge>
                    {persona && (
                      <div
                        className={cn(
                          'w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-bold',
                          persona.color
                        )}
                      >
                        {persona.initials}
                      </div>
                    )}
                  </div>
                  <h2 className="text-xl font-bold">{chapter.title}</h2>
                  <p className="text-sm text-muted-foreground">{chapter.subtitle}</p>
                </div>

                {/* Chapter content */}
                <Content />
              </motion.div>
            </AnimatePresence>

            {/* Navigation buttons */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t">
              <Button
                variant="outline"
                onClick={goPrev}
                disabled={currentChapter === 0}
                className="gap-2"
              >
                <ChevronLeft className="w-4 h-4" />
                Anterior
              </Button>
              <span className="text-xs text-muted-foreground">
                {currentChapter + 1} / {CHAPTERS.length}
              </span>
              <Button
                onClick={goNext}
                disabled={currentChapter === CHAPTERS.length - 1}
                className="gap-2"
              >
                Siguiente
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

export default TutorialPage;
